import React, { useState, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Mic, Loader2, AlertCircle, RefreshCw, Languages, ChevronDown } from 'lucide-react';
import { useTranscription, TranscriptionSegment } from '@/hooks/useTranscription';
import { useTranscriptionStorage } from '@/hooks/useTranscriptionStorage';
import { useScripts } from '@/hooks/useScripts';
import { SubtitleEditor } from './SubtitleEditor';

interface TranscriptionPanelProps {
  audioBlob?: Blob;
  recordingId?: string; // 可選 - 本地錄音沒有 recordingId
  scriptId?: string;
  filename?: string;
  onTranscriptionComplete?: (segments: TranscriptionSegment[]) => void;
}

const LANGUAGE_OPTIONS = [
  { value: 'zh', labelKey: 'transcription.languages.zh' },
  { value: 'en', labelKey: 'transcription.languages.en' },
  { value: 'ja', labelKey: 'transcription.languages.ja' },
  { value: 'ko', labelKey: 'transcription.languages.ko' },
  { value: 'yue', labelKey: 'transcription.languages.yue' },
] as const;

/**
 * 轉錄主面板
 * 包含轉錄按鈕、語言選擇、進度顯示、字幕編輯區域
 */
export function TranscriptionPanel({
  audioBlob,
  recordingId,
  scriptId,
  filename,
  onTranscriptionComplete,
}: TranscriptionPanelProps) {
  const { t } = useTranslation();
  const [language, setLanguage] = useState<string>('zh');
  const [segments, setSegments] = useState<TranscriptionSegment[]>([]);
  const [duration, setDuration] = useState<number | null>(null);

  const {
    status,
    progress,
    error: transcriptionError,
    transcribe,
    reset,
    isTranscribing,
  } = useTranscription();

  const {
    saveTranscription,
    fetchTranscription,
    loading: saving,
  } = useTranscriptionStorage();

  const { getScript } = useScripts();

  // 載入已有轉錄
  const handleLoadExisting = useCallback(async () => {
    if (!recordingId) return;
    const existing = await fetchTranscription(recordingId);
    if (existing) {
      setSegments(existing.segments);
      setDuration(existing.duration);
      setLanguage(existing.language);
      console.log('[Transcription] Loaded existing segments');
    }
  }, [recordingId, fetchTranscription]);

  // 自動載入組件掛載時已有的轉錄
  useEffect(() => {
    if (recordingId) {
      handleLoadExisting();
    }
  }, [recordingId]); // 只在 recordingId 變化或掛載時執行一次

  // 開始轉錄
  const handleTranscribe = useCallback(async () => {
    if (!audioBlob) return;

    reset();

    // 獲取腳本內容作為 Prompt，輔助 Whisper 對齊時間戳
    let prompt = '';
    if (scriptId) {
      try {
        const script = await getScript(scriptId);
        if (script?.content) {
          prompt = script.content;
          console.log('[Transcription] Using script as prompt:', script.title);
        }
      } catch (err) {
        console.warn('[Transcription] Failed to fetch script for prompt:', err);
      }
    }

    const result = await transcribe(audioBlob, language, recordingId, prompt);

    if (result) {
      setSegments(result.segments);
      setDuration(result.duration);
      onTranscriptionComplete?.(result.segments);

      // 成功後自動儲存 (Auto-save)
      if (recordingId) {
        console.log('[Transcription] Auto-saving new segments');
        await saveTranscription(recordingId, result.segments, {
          scriptId,
          language,
          duration: result.duration,
        });
      }
    }
  }, [audioBlob, language, recordingId, scriptId, transcribe, reset, onTranscriptionComplete, getScript, saveTranscription]);

  // 儲存
  const handleSave = useCallback(async () => {
    if (!recordingId) return;
    await saveTranscription(recordingId, segments, {
      scriptId,
      language,
      duration: duration || undefined,
    });
  }, [recordingId, segments, scriptId, language, duration, saveTranscription]);

  // 已有轉錄結果
  const hasSegments = segments.length > 0;

  return (
    <div className="flex flex-col gap-4">
      {/* 控制區 */}
      {!hasSegments && (
        <div className="border-2 border-black rounded-xl bg-white p-6 shadow-[4px_4px_0_#1A1A1A] mb-4">
          <div className="flex flex-col gap-6">
            <div className="flex flex-wrap gap-4 items-center justify-between">
              {/* 語言選擇 */}
              <div className="flex items-center gap-2">
                <div className="relative group">
                  <div className="absolute left-4 top-1/2 -translate-y-1/2 text-black z-10">
                    <Languages className="w-5 h-5" />
                  </div>
                  <select
                    value={language}
                    onChange={(e) => setLanguage(e.target.value)}
                    disabled={isTranscribing}
                    className="
                      appearance-none min-w-[140px] pl-11 pr-10 py-3 bg-white border-2 border-black rounded-full 
                      font-bold text-base cursor-pointer disabled:bg-gray-100 disabled:cursor-not-allowed
                      focus:outline-none focus:ring-2 focus:ring-[#FF6B9D] focus:ring-offset-2
                      transition-all hover:bg-gray-50 shadow-[2px_2px_0_#1A1A1A]
                    "
                  >
                    {LANGUAGE_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {t(opt.labelKey)}
                      </option>
                    ))}
                  </select>
                  <div className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none text-black">
                    <ChevronDown className="w-4 h-4 text-gray-400" />
                  </div>
                </div>
              </div>

              <div className="flex gap-3">
                {/* 載入已有 - Secondary */}
                <button
                  onClick={handleLoadExisting}
                  disabled={isTranscribing}
                  className="
                    flex items-center gap-2 px-5 py-3
                    bg-white text-black font-bold
                    border-2 border-black rounded-xl
                    hover:bg-gray-100 transition-colors
                    disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed
                  "
                >
                  <RefreshCw className="w-5 h-5" />
                  {t('transcription.panel.loadExisting')}
                </button>

                {/* 轉錄按鈕 - Primary */}
                <button
                  onClick={handleTranscribe}
                  disabled={!audioBlob || isTranscribing}
                  className="
                    flex items-center gap-2 px-8 py-3
                    bg-[#FF6B9D] text-black font-bold
                    border-2 border-black rounded-xl
                    hover:translate-x-0.5 hover:translate-y-0.5
                    hover:shadow-[4px_4px_0_#1A1A1A]
                    active:translate-x-1 active:translate-y-1 active:shadow-none
                    transition-all duration-100
                    disabled:bg-gray-300 disabled:text-gray-500 disabled:cursor-not-allowed
                    disabled:hover:translate-x-0 disabled:hover:translate-y-0 disabled:hover:shadow-none
                  "
                >
                  {isTranscribing ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      {t('transcription.panel.transcribing')}
                    </>
                  ) : (
                    <>
                      <Mic className="w-5 h-5" />
                      {t('transcription.panel.generateSubtitle')}
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>

          {/* 進度條 */}
          {isTranscribing && (
            <div className="mt-4">
              <div className="h-2 bg-white border-2 border-black rounded-full overflow-hidden">
                <div
                  className="h-full bg-[#FF6B9D] transition-all duration-300"
                  style={{ width: `${progress}%` }}
                />
              </div>
              <p className="text-sm text-gray-600 mt-2 text-center">
                {status === 'uploading' && t('transcription.panel.uploading')}
                {status === 'transcribing' && t('transcription.panel.processing')}
              </p>
            </div>
          )}

          {/* 錯誤訊息 */}
          {transcriptionError && (
            <div className="mt-4 flex items-center gap-2 p-3 bg-[#FF6B9D]/20 border-2 border-[#FF6B9D] rounded-xl">
              <AlertCircle className="w-5 h-5 text-red-600" />
              <p className="text-sm text-red-600">{transcriptionError}</p>
              <button
                onClick={handleTranscribe}
                className="ml-auto text-sm font-medium text-red-600 hover:underline"
              >
                {t('transcription.panel.retry')}
              </button>
            </div>
          )}
        </div>
      )}

      {/* 編輯區 */}
      {hasSegments && (
        <SubtitleEditor
          segments={segments}
          onChange={setSegments}
          onSave={handleSave}
          filename={filename}
          saving={saving}
        />
      )}
    </div>
  );
}
